import React, { useState, useRef, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import InputText from '../../../component/input/InputText';
import InputNumber from '../../../component/input/InputNumber';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import { useContext } from 'react';
import { MessageContext } from '../../../context/MessageContext';
import './CreatePlanogramDialog.scss';

const CreatePlanogramDialog = ({
    visible,
    handleSubmit: onHandleSubmit,
    onHide
}) => {
    const { t } = useTranslation();
    const { showErrorMessage, showSuccessMessage } = useContext(MessageContext);
    const saveButtonRef = useRef(null);
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        systemId: '',
        externalSystemId: '',
        rows: 5,
        columns: 5
    });

    useEffect(() => {
        if (visible) {
            setFormData({ name: '', systemId: '', externalSystemId: '', rows: 5, columns: 5 });
            setIsSaving(false);
        }
    }, [visible]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleNumberChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value ?? prev[field]
        }));
    };

    const handleSubmit = async () => {
        if (formData.name.trim() && formData.rows >= 1 && formData.columns >= 1) {
            setIsSaving(true);
            try {
                onHandleSubmit(formData);
            } catch (error) {
                console.error('Error creating planogram:', error);
                showErrorMessage(t('planogram.create.error'));
            } finally {
                setIsSaving(false);
            }
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSubmit();
        }
    };

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            header={t('planogram.create.title')}
            className="create-planogram-dialog"
            closable={true}
            closeOnEscape={true}
            modal={true}
            draggable={false}
            resizable={false}
            focusOnShow={true}
            autoFocus={true}
            onShow={() => {
                // Focus will go to first focusable, handled by Dialog
            }}
            footer={
                <div className="flex w-full gap-3 justify-content-end">
                    <Button
                        ref={saveButtonRef}
                        label={t('planogram.create.save')}
                        onClick={handleSubmit}
                        disabled={!formData.name.trim() || formData.rows < 1 || formData.columns < 1 || isSaving}
                        loading={isSaving}
                        className='w-full'
                    />
                </div>
            }
        >
            <div className="flex flex-column gap-5">
                <div className="text-color-secondary text-sm line-height-3 m-0">
                    {t('planogram.create.description')}
                </div>

                <div className="flex flex-column gap-4 w-full">
                    {/* Name and Planogram ID Inputs */}
                    <div className="flex gap-3">

                        {/* Planogram ID Input */}
                        <div className="flex flex-column gap-2 flex-1">
                            <label className="font-semibold text-color-secondary text-sm">
                                {t('planogram.create.planogramSystemId')}
                            </label>
                            <InputText
                                value={formData.systemId}
                                onChange={(e) => handleInputChange('systemId', e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        if (saveButtonRef.current && !saveButtonRef.current.disabled) {
                                            saveButtonRef.current.click();
                                        }
                                    }
                                }}
                                className="w-full"
                                disabled={true}
                                maxlength={50}
                            />
                        </div>

                        {/* External System ID Input */}
                        <div className="flex flex-column gap-2 flex-1">
                            <label className="font-semibold text-color-secondary text-sm">
                                {t('planogram.create.planogramId')}
                            </label>
                            <InputText
                                value={formData.externalSystemId}
                                onChange={(e) => handleInputChange('externalSystemId', e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        if (saveButtonRef.current && !saveButtonRef.current.disabled) {
                                            saveButtonRef.current.click();
                                        }
                                    }
                                }}
                                placeholder={t('planogram.create.planogramIdPlaceholder')}
                                className="w-full"
                                maxlength={50}
                            />
                        </div>
                    </div>

                    {/* Rows and Columns Counters */}
                    <div className="flex gap-3">
                        {/* Name Input */}
                        <div className="flex flex-column gap-2 flex-1">
                            <label className="font-semibold text-color-secondary text-sm">
                                {t('planogram.create.name')}
                            </label>
                            <InputText
                                value={formData.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        if (saveButtonRef.current && !saveButtonRef.current.disabled) {
                                            saveButtonRef.current.click();
                                        }
                                    }
                                }}
                                placeholder={t('planogram.create.namePlaceholder')}
                                className="w-full"
                                maxlength={50}
                            />
                        </div>
                    </div>

                    {/* Rows and Columns Counters */}
                    <div className="flex gap-3">
                        {/* Rows Counter */}
                        <div className="flex flex-column gap-2 flex-1">
                            <label className="font-semibold text-color-secondary text-sm">
                                {t('planogram.create.rows')}
                            </label>
                            <InputNumber
                                value={formData.rows}
                                onValueChange={(e) => handleNumberChange('rows', e.value)}
                                showButtons buttonLayout="horizontal"
                                step={1} min={1} max={20}
                                useGrouping={false}
                                className="w-full"
                            />
                        </div>

                        {/* Columns Counter */}
                        <div className="flex flex-column gap-2 flex-1">
                            <label className="font-semibold text-color-secondary text-sm">
                                {t('planogram.create.columns')}
                            </label>
                            <InputNumber
                                value={formData.columns}
                                onValueChange={(e) => handleNumberChange('columns', e.value)}
                                showButtons buttonLayout="horizontal"
                                step={1} min={1} max={20}
                                useGrouping={false}
                                className="w-full"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </Dialog>
    );
};

export default CreatePlanogramDialog;
